import {
  ExpensesListingGetters,
  ExpensesListingActions,
  ExpensesListingMutations
} from '@/app/features/expenses/features/expenses-listing/expenses-listing.keys';
import { expensesListingService as expensesService } from '@/app/features/expenses/features/expenses-listing/expenses-listing.service';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';
import i18n from '@/i18n';

export const getInitialState = () => {
  return {
    expensesBulkAction: null,
    expensesAssignments: [],
    expensesContractors: [],
    expensesFilteredSortedRecordCount: 0,
    expensesSearchParam: '',
    batchExpensesList: [],
    expensesFilters: {
      contractorId: 0,
      assignmentId: 0,
      dateRangeFilters: {
        startDate: null,
        endDate: null
      },
      statusFilters: {
        draft: false,
        submitted: false,
        approved: false,
        processed: false,
        rejected: false,
        obsolete: false,
        expired: false
      }
    },
    expensesSortOrder: {
      sortBy: 'startDate',
      sortDirection: 'desc'
    },
    expensesIsLoading: false,
    expenses: [],
    modifiedExpenses: {},
    expensesStatusCounts: {
      draft: 0,
      submitted: 0,
      approved: 0,
      processed: 0,
      rejected: 0,
      obsolete: 0,
      expired: 0
    },
    expensesPaginationParameters: {
      recordsPerPage: 10,
      offset: 0
    }
  };
};

export const expensesListingStore = {
  state: getInitialState(),
  getters: {
    [ExpensesListingGetters.ExpensesSearchParam]: state => {
      return state.expensesSearchParam;
    },
    [ExpensesListingGetters.SelectedExpenses]: state => param => {
      const selectedExpenses = state.expenses.filter(expense => expense.isSelected === true);
      return selectedExpenses.map(expense => expense[param]);
    },
    [ExpensesListingGetters.ModifiedExpenses]: state => {
      return state.modifiedExpenses;
    },
    [ExpensesListingGetters.ExpensesPerPage]: state => {
      return state.expensesPaginationParameters.recordsPerPage;
    },
    [ExpensesListingGetters.ExpensesListingOffset]: state => {
      return state.expensesPaginationParameters.offset;
    },
    [ExpensesListingGetters.ExpensesFilteredSortedRecordCount]: state => {
      return state.expensesFilteredSortedRecordCount;
    },
    [ExpensesListingGetters.ExpensesContractors]: state => {
      return state.expensesContractors;
    },
    [ExpensesListingGetters.ExpenseIsLoading]: state => {
      return state.expensesIsLoading;
    },
    [ExpensesListingGetters.GetBatchExpensesList]: state => {
      return state.batchExpensesList;
    }
  },
  mutations: {
    [ExpensesListingMutations.SetExpensesSearchParam]: function(state, searchParam) {
      state.expensesSearchParam = searchParam;
    },
    [ExpensesListingMutations.SetExpensesFilters]: function(state, filters) {
      state.expensesFilters = filters;
    },
    [ExpensesListingMutations.SetExpensesSortOrder]: function(state, sortOrder) {
      state.expensesSortOrder = sortOrder;
    },
    [ExpensesListingMutations.SetExpensesLoading]: function(state, isLoading = true) {
      state.expensesIsLoading = isLoading;
    },
    [ExpensesListingMutations.RetrieveExpensesAssignmentsSuccess]: function(state, assignments) {
      state.expensesAssignments = assignments;
    },
    [ExpensesListingMutations.RetrieveExpensesContractorsSuccess]: function(state, contractors) {
      state.expensesContractors = contractors;
    },
    [ExpensesListingMutations.RetrieveExpensesSuccess]: function(state, expenses) {
      //add 'isSelected' field to expense object to support bulk actions
      const modifiedExpenses = expenses.map(expense => {
        return { ...expense, isSelected: false };
      });

      state.expenses = modifiedExpenses;
    },
    [ExpensesListingMutations.RetrieveExpensesStatusCountsSuccess]: function(
      state,
      expensesStatusCounts
    ) {
      state.expensesStatusCounts = expensesStatusCounts;
    },
    [ExpensesListingMutations.ResetExpensesState]: state => {
      Object.assign(state, getInitialState());
    },
    [ExpensesListingMutations.SetExpensesBulkActionValue]: (state, bulkAction) => {
      state.expensesBulkAction = bulkAction;
    },
    [ExpensesListingMutations.UpdateSelectedExpenses]: (state, payload) => {
      if (typeof payload === 'string') {
        const idx = state.expenses.findIndex(expense => expense.referenceNumber === payload);
        const selectedExpense = state.expenses[idx];

        //toggle the 'isSelected' field of chosen item
        selectedExpense.isSelected = !selectedExpense.isSelected;

        //modify state
        state.expenses[idx] = selectedExpense;
      } else if (typeof payload === 'object') {
        state.expenses.forEach(expense => (expense.isSelected = payload.checked));
      }
    },
    [ExpensesListingMutations.SetExpensesModifiedWithBulkAction]: (state, referenceNumbers) => {
      const toUpdate = Object.assign({}, state.modifiedExpenses);

      //Create a map of reference numbers of modified items
      //This will be used to lookup item reference numbers to determine
      //if 'is-modified' class shoud be attached to checkboxes in ExpensesTable component
      const refMap = referenceNumbers.reduce((obj, referenceNumber, index) => {
        obj[referenceNumber] = index;
        return obj;
      }, {});

      state.modifiedExpenses = { ...toUpdate, ...refMap };
    },
    [ExpensesListingMutations.ResetExpensesState]: state => {
      Object.assign(state, getInitialState());
    },
    [ExpensesListingMutations.UpdateExpensesPaginationParameters]: (state, params) => {
      state.expensesPaginationParameters.recordsPerPage = params.recordsPerPage;
      state.expensesPaginationParameters.offset = params.offset;
    },
    [ExpensesListingMutations.ResetExpensesPaginationOffset]: state => {
      state.expensesPaginationParameters.offset = 0;
    },
    [ExpensesListingMutations.SetExpensesFilteredSortedRecordCount]: (state, recordCount) => {
      state.expensesFilteredSortedRecordCount = recordCount.count;
    },
    [ExpensesListingMutations.SetBatchExpensesList]: (state, expensesList) => {
      state.batchExpensesList = expensesList;
    }
  },
  actions: {
    [ExpensesListingActions.SetExpensesFilters]: function({ commit, dispatch }, filters) {
      commit(ExpensesListingMutations.SetExpensesFilters, filters);
      commit(ExpensesListingMutations.ResetExpensesPaginationOffset);
      dispatch(ExpensesListingActions.RetrieveExpenses);
    },

    [ExpensesListingActions.SetExpensesSortOrder]: function({ commit, dispatch }, sortOrder) {
      commit(ExpensesListingMutations.SetExpensesSortOrder, sortOrder);
      commit(ExpensesListingMutations.ResetExpensesPaginationOffset);
      dispatch(ExpensesListingActions.RetrieveExpenses);
    },

    [ExpensesListingActions.RetrieveExpensesAssignments]: function({ commit, dispatch }) {
      commit(ExpensesListingMutations.SetExpensesLoading);

      return expensesService
        .retrieveAssignments()
        .then(assignments =>
          commit(ExpensesListingMutations.RetrieveExpensesAssignmentsSuccess, assignments)
        )
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('expenses.listing.errors.retrieveAssignments')
          });
          commit(ExpensesListingMutations.SetExpensesLoading, false);
        });
    },
    [ExpensesListingActions.RetrieveExpensesContractors]: function({ commit, dispatch }) {
      commit(ExpensesListingMutations.SetExpensesLoading);

      return expensesService
        .retrieveContractors()
        .then(contractors =>
          commit(ExpensesListingMutations.RetrieveExpensesContractorsSuccess, contractors)
        )
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('expenses.listing.errors.retrieveContractors')
          });
          commit(ExpensesListingMutations.SetExpensesLoading, false);
        });
    },
    [ExpensesListingActions.RetrieveExpenses]: function(
      { commit, state, dispatch, getters },
      afterBulkAction = false
    ) {
      commit(ExpensesListingMutations.SetExpensesLoading);
      const modifiedExpenses = getters.SelectedExpenses('referenceNumber');

      if (afterBulkAction)
        commit(ExpensesListingMutations.SetExpensesModifiedWithBulkAction, modifiedExpenses);
      return expensesService
        .retrieveExpenses(
          state.expensesFilters,
          state.expensesSortOrder,
          state.expensesPaginationParameters,
          state.expensesSearchParam
        )
        .then(expenses => {
          dispatch(ExpensesListingActions.RetrieveExpensesFilteredSortedRecordCount);
          commit(ExpensesListingMutations.RetrieveExpensesSuccess, expenses);
        })
        .then(() => {
          commit(ExpensesListingMutations.SetExpensesLoading, false);
        })
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('expenses.listing.errors.retrieveExpenses')
          });
          commit(ExpensesListingMutations.SetExpensesLoading, false);
        });
    },
    [ExpensesListingActions.RetrieveExpensesStatusCounts]: function({ commit, dispatch }) {
      commit(ExpensesListingMutations.SetExpensesLoading);

      return expensesService
        .retrieveStatusCounts()
        .then(expenseStatusCounts =>
          commit(ExpensesListingMutations.RetrieveExpensesStatusCountsSuccess, expenseStatusCounts)
        )
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('expenses.listing.errors.retrieveStatusCount')
          });
          commit(ExpensesListingMutations.SetExpensesLoading, false);
        });
    },
    [ExpensesListingActions.ExportExpensesAsExcel]: function({ state }) {
      return expensesService.downloadExpensesXlsx(state.expensesFilters, state.expensesSortOrder);
    },
    [ExpensesListingActions.SelectExpense]: ({ commit }, referenceNumber) => {
      commit(ExpensesListingMutations.UpdateSelectedExpenses, referenceNumber);
    },
    [ExpensesListingActions.SelectDeselectAllExpenses]: ({ commit }, isSelected) => {
      commit(ExpensesListingMutations.UpdateSelectedExpenses, isSelected);
    },
    [ExpensesListingActions.SetExpensesBulkAction]: ({ commit }, bulkAction) => {
      commit(ExpensesListingMutations.SetExpensesBulkActionValue, bulkAction);
    },
    [ExpensesListingActions.DoExpensesBulkAction]: ({ dispatch, commit, getters }, bulkAction) => {
      const selectedExpenseRefs = getters.SelectedExpenses('referenceNumber');
      commit(ExpensesListingMutations.SetExpensesLoading);
      return expensesService
        .bulkUpdateExpenses(bulkAction, selectedExpenseRefs)
        .then(() => {
          dispatch(ExpensesListingActions.RetrieveExpenses, true);
        })
        .catch(error => {
          console.trace(error);
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('expenses.listing.errors.bulkApproveRejectFailed')
          });
        });
    },
    [ExpensesListingActions.SetInitialExpensesState]: ({ commit }) => {
      commit(ExpensesListingMutations.ResetExpensesState);
    },
    [ExpensesListingActions.SetExpensesPaginationParameters]: ({ commit, dispatch }, params) => {
      commit(ExpensesListingMutations.UpdateExpensesPaginationParameters, params);
      dispatch(ExpensesListingActions.RetrieveExpensesFilteredSortedRecordCount);
      dispatch(ExpensesListingActions.RetrieveExpenses);
    },
    [ExpensesListingActions.UpdateExpensesSearchParam]: ({ commit, dispatch }, payload) => {
      commit(ExpensesListingMutations.SetExpensesSearchParam, payload.searchParam);
      commit(ExpensesListingMutations.SetExpensesLoading);
      return dispatch(ExpensesListingActions.RetrieveExpensesFilteredSortedRecordCount)
        .then(() => commit(ExpensesListingMutations.RetrieveExpensesSuccess, payload.expenses))
        .then(() => commit(ExpensesListingMutations.SetExpensesLoading, false));
    },
    [ExpensesListingActions.RetrieveExpensesFilteredSortedRecordCount]: function({
      commit,
      state,
      dispatch
    }) {
      return expensesService
        .retrieveFilteredSortedRecordCount(
          state.expensesFilters,
          state.expensesSortOrder,
          state.expensesSearchParam
        )
        .then(recordCount => {
          commit(ExpensesListingMutations.SetExpensesFilteredSortedRecordCount, recordCount);
        })
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('expenses.listing.errors.retrieveExpenses')
          });
        });
    },
    [ExpensesListingActions.SetBatchExpensesList]: ({ commit }, expensesList) => {
      commit(ExpensesListingMutations.SetBatchExpensesList, expensesList);
    }
  }
};
